import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Table, Button, Modal, Input, message, Tag, Alert } from 'antd'
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  MinusCircleFilled,
  PlusOutlined,
  EditOutlined,
} from '@ant-design/icons'
import { ROLES } from 'const/roles'
import { newAdminRoles } from 'domains/admin/selectors'
import useAction from 'utils/useAction'
import {
  fetchEarnOffers,
  createCompliantEarnGoal,
  updateEarnGoal,
} from 'actions/views/users'
import { clearCompliantEarnGoalFeedback } from 'domains/user/actions'
import {
  getEarnOffers,
  getEarnOffersLoaded,
  getEarnGoalSubmitting,
  getCompliantEarnGoalResult,
  getCompliantEarnGoalError,
} from 'domains/user/selectors'
import moment from 'moment'
import { PROGRAM_STATUS_COLORS } from 'const/earnPrograms'

const DATE_TIME_FORMAT = 'YYYY-MM-DD HH:mm:ss'

const EarnOffersModal = ({ userId }) => {
  const userRoles = useSelector(newAdminRoles)
  const offers = useSelector(getEarnOffers)
  const areOffersLoaded = useSelector(getEarnOffersLoaded)
  const isSubmitting = useSelector(getEarnGoalSubmitting)
  const compliantGoalResult = useSelector(getCompliantEarnGoalResult)
  const compliantGoalError = useSelector(getCompliantEarnGoalError)

  const fetchEarnOffersFunc = useAction(fetchEarnOffers)
  const createCompliantEarnGoalFunc = useAction(createCompliantEarnGoal)
  const updateEarnGoalFunc = useAction(updateEarnGoal)
  const clearCompliantEarnGoalFeedbackFunc = useAction(
    clearCompliantEarnGoalFeedback
  )

  const [expandedRowKeys, setExpandedRowKeys] = useState([])
  const [goalForm, setGoalForm] = useState(null)
  const [formReward, setFormReward] = useState('')
  const goalFormMode = goalForm?.mode

  useEffect(() => {
    if (userId) {
      fetchEarnOffersFunc(userId)
    }
  }, [fetchEarnOffersFunc, userId])

  const handleCreateGoal = (offer, goal) => {
    clearCompliantEarnGoalFeedbackFunc()
    setGoalForm({
      mode: 'create',
      userId,
      offerId: offer.id,
      goalId: goal.id,
      goalName: goal.attributes.goal_id,
    })
    setFormReward('')
  }

  const handleEditGoal = (goal) => {
    clearCompliantEarnGoalFeedbackFunc()
    setGoalForm({
      mode: 'edit',
      userGoalId: goal.user_goal.id,
      goalName: goal.attributes.goal_id,
    })
    setFormReward(goal.user_goal.attributes.reward || '')
  }

  const handleSubmitGoalForm = () => {
    if (goalForm.mode === 'create') {
      if (!goalForm.userId || !goalForm.offerId || !goalForm.goalId) {
        message.warning('Missing required goal identifiers.')
        return
      }

      createCompliantEarnGoalFunc({
        userId: goalForm.userId,
        userNetworkProgramId: goalForm.offerId,
        networkProgramGoalId: goalForm.goalId,
      })
      return
    }

    if (!formReward.trim()) {
      message.warning('Please enter a reward value.')
      return
    }

    updateEarnGoalFunc({
      userGoalId: goalForm.userGoalId,
      reward: formReward,
      userId,
    })

    setGoalForm(null)
    setFormReward('')
  }

  useEffect(() => {
    if (goalFormMode === 'create' && compliantGoalResult?.valid) {
      setGoalForm(null)
      setFormReward('')
      clearCompliantEarnGoalFeedbackFunc()
    }
  }, [
    clearCompliantEarnGoalFeedbackFunc,
    compliantGoalResult,
    goalFormMode,
  ])

  const handleCancelGoalForm = () => {
    setGoalForm(null)
    setFormReward('')
    clearCompliantEarnGoalFeedbackFunc()
  }

  const goalsColumns = (offer) => [
    {
      title: 'Goal ID',
      dataIndex: ['attributes', 'goal_id'],
      key: 'goal_id',
    },
    {
      title: 'Name',
      dataIndex: ['attributes', 'name'],
      key: 'name',
    },
    {
      title: 'Type',
      dataIndex: ['attributes', 'type'],
      key: 'type',
    },
    {
      title: 'Achieved',
      key: 'achieved',
      render: (_, goal) => {
        const complianceStatus = goal.user_goal?.attributes?.compliance_status
        if (
          complianceStatus === 'suspected_fraud' ||
          complianceStatus === 'definite_fraud'
        ) {
          return (
            <MinusCircleFilled
              style={{ color: '#fa8c16', fontSize: 18 }}
              title={complianceStatus.replace(/_/g, ' ')}
            />
          )
        }
        return goal.achieved ? (
          <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 18 }} />
        ) : (
          <CloseCircleOutlined style={{ color: '#f5222d', fontSize: 18 }} />
        )
      },
    },
    {
      title: 'Reward',
      key: 'reward',
      render: (_, goal) => goal.user_goal?.attributes?.reward ?? '—',
    },
    {
      title: 'Compliance Status',
      key: 'compliance_status',
      render: (_, goal) =>
        goal.user_goal?.attributes?.compliance_status ? (
          <Tag
            color={
              goal.user_goal.attributes.compliance_status === 'compliant'
                ? 'green'
                : 'orange'
            }
          >
            {goal.user_goal.attributes.compliance_status}
          </Tag>
        ) : (
          '—'
        ),
    },
    ...((userRoles.includes(ROLES.super) || userRoles.includes(ROLES.dev))
      ? [{
          title: 'Actions',
          key: 'actions',
          render: (_, goal) =>
            goal.user_goal === null ? (
              <Button
                size="small"
                icon={<PlusOutlined />}
                onClick={() => handleCreateGoal(offer, goal)}
              >
                Create
              </Button>
            ) : (
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => handleEditGoal(goal)}
              >
                Edit
              </Button>
            ),
        }]
      : []),
  ]

  const offersColumns = [
    {
      title: 'Game Name',
      key: 'game_name',
      render: (_, record) => record.network_program?.attributes?.name || '—',
    },
    {
      title: 'Network',
      key: 'preferred_network',
      render: (_, record) => {
        const network = record.network_program?.attributes?.preferred_network
        return network ? <Tag color="blue">{network}</Tag> : '—'
      },
    },
    {
      title: 'Program Status',
      key: 'program_status',
      render: (_, record) => {
        const status = record.network_program?.attributes?.program_status
        return status ? (
          <Tag color={PROGRAM_STATUS_COLORS[status] || 'default'}>
            {status.replace(/_/g, ' ')}
          </Tag>
        ) : '—'
      },
    },
    {
      title: 'Platform',
      key: 'website_id',
      render: (_, record) => record.network_program?.attributes?.website_id || '—',
    },
    {
      title: 'Action',
      dataIndex: ['attributes', 'action'],
      key: 'action',
    },
    {
      title: 'Installed At',
      key: 'created_at',
      render: (_, record) =>
        record.attributes?.created_at
          ? moment(record.attributes.created_at).format(DATE_TIME_FORMAT)
          : '—',
    },
  ]

  const expandedRowRender = (offer) => (
    <Table
      dataSource={offer.goals || []}
      columns={goalsColumns(offer)}
      rowKey="id"
      pagination={false}
      size="small"
    />
  )

  return (
    <div>
      <Table
        dataSource={offers || []}
        columns={offersColumns}
        rowKey="id"
        loading={!areOffersLoaded}
        pagination={false}
        expandable={{
          expandedRowRender,
          expandedRowKeys,
          onExpand: (expanded, record) => {
            setExpandedRowKeys(
              expanded
                ? [...expandedRowKeys, record.id]
                : expandedRowKeys.filter((key) => key !== record.id)
            )
          },
        }}
        scroll={{ x: 'max-content' }}
      />

      <Modal
        title={
          goalForm?.mode === 'create'
            ? `Create Compliant Goal: ${goalForm?.goalName}`
            : `Edit Goal: ${goalForm?.goalName}`
        }
        open={!!goalForm}
        onOk={handleSubmitGoalForm}
        onCancel={handleCancelGoalForm}
        confirmLoading={isSubmitting}
        okText={goalForm?.mode === 'create' ? 'Create compliant goal' : 'Update'}
      >
        {goalForm?.mode === 'create' && (
          <>
            {compliantGoalError?.message && (
              <Alert
                type="error"
                showIcon
                message={compliantGoalError.message}
                description={
                  compliantGoalError.code
                    ? `Error code: ${compliantGoalError.code}`
                    : undefined
                }
                style={{ marginBottom: 16 }}
              />
            )}
            <div style={{ marginBottom: 12 }}>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label htmlFor="earn-goal-user-id">User ID:</label>
              <Input
                id="earn-goal-user-id"
                value={goalForm?.userId ?? ''}
                disabled
                style={{ marginTop: 4 }}
              />
            </div>
            <div style={{ marginBottom: 12 }}>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label htmlFor="earn-goal-program-id">User Network Program ID:</label>
              <Input
                id="earn-goal-program-id"
                value={goalForm?.offerId ?? ''}
                disabled
                style={{ marginTop: 4 }}
              />
            </div>
            <div>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label htmlFor="earn-goal-network-goal-id">Network Program Goal ID:</label>
              <Input
                id="earn-goal-network-goal-id"
                value={goalForm?.goalId ?? ''}
                disabled
                style={{ marginTop: 4 }}
              />
            </div>
          </>
        )}
        {goalForm?.mode === 'edit' && (
          <p style={{ color: '#faad14', marginBottom: 12 }}>
            Note: Updating the reward will reset the achievement status for this
            goal.
          </p>
        )}
        {goalForm?.mode === 'edit' && (
          <div>
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor="earn-goal-reward">Reward:</label>
            <Input
              id="earn-goal-reward"
              value={formReward}
              onChange={(e) => setFormReward(e.target.value)}
              placeholder="Enter reward value"
              style={{ marginTop: 4 }}
            />
          </div>
        )}
      </Modal>
    </div>
  )
}

export default EarnOffersModal
